// Veluga Service Platform — Phase 1 service foundation types.
// Identity, tenancy, entitlement, model catalog, gateway backend, and route
// contracts shared by service-api, service-client, desktop main, and admin.
//
// HARD CONSTRAINT: secret-bearing fields (`baseUrlRef`, `credentialRef`,
// `endpointRef`, `litellmModelName`, `upstreamModelRef`) must never appear in a
// `DesktopBootstrap` or any renderer IPC payload. Only the Gateway Facade
// resolves a Veluga model id into a backend route.

import type { Clearance, PolicyContextSnapshot } from './policy.js';
import type { AgentPermissionMode } from './permission-mode.js';
import type { QuotaCycle, QuotaPolicyProvenance } from './quota.js';
import type { AdaptiveTaskKind, AllowedAdaptiveTaskKind } from './routing.js';
import type { RealtimeVoiceMetadata, RealtimeVoicePolicy, DesktopRealtimeVoiceAvailability, DesktopVoiceAvailability, SpeechModelMetadata, TenantSpeechPolicy } from './realtime.js';
import type { DesktopModelView, DesktopWebSearchAvailability, ModelInteractionKind, ProviderProtocol, RouteKind, ThinkingAutoCapability, ThinkingLevel, VelugaModelCapabilities, VelugaModelProvider, VelugaRouteMode, WebSearchNativeProvider } from './desktop-model.js';
import type { RequiredConsent } from './desktop-consent.js';

export type { DesktopModelView, DesktopWebSearchAvailability, ProviderProtocol, RouteKind, ThinkingAutoCapability, ThinkingLevel, VelugaModelCapabilities, VelugaModelProvider, VelugaRouteMode, WebSearchNativeProvider } from './desktop-model.js';
export { DEFAULT_WEB_SEARCH_MAX_USES, MIN_WEB_SEARCH_MAX_USES, MAX_WEB_SEARCH_MAX_USES, clampWebSearchMaxUses } from './desktop-model.js';
export type { ConsentKind, RequiredConsent } from './desktop-consent.js';
export type { ExtensionRequest, HwpExtensionRequest, UnsignedExtensionManifest, UnsignedHwpExtensionManifest, HwpExtensionManifest, ExtensionManifest } from './extension-manifest.js';

export type ServiceRole =
  | 'owner'
  | 'admin'
  | 'billing_admin'
  | 'member'
  | 'auditor'
  | 'platform_admin'
  | 'partner_admin';
export type DeploymentProfile = 'hosted' | 'on-prem';

/** A reseller/aggregator that sits between the platform and its tenants. The
 *  platform creates partners, designates their admins (in the separate
 *  `partner_admins` registry), and caps how many tenants they may create. A
 *  partner owns the tenants it creates (`Tenant.partnerId`). */
export interface Partner {
  id: string;
  name: string;
  slug: string;
  /** Max tenants this partner may create. `undefined`/null = unlimited. */
  tenantCap?: number;
  status: 'active' | 'suspended';
  /** Platform-controlled: may this partner's tenants connect BYO custom models? */
  allowCustomModels?: boolean;
}

/** How a catalog model's usage settles against credit. */
export type BillingMode =
  'platform-credit' | 'byo-unbilled' | 'byo-token-budget';

export interface Tenant {
  realtimeVoicePolicy?: RealtimeVoicePolicy;
  speechPolicy?: TenantSpeechPolicy;
  /** Tenant voice policy version; absent = 0. Independent of the global featureRevision. */
  voicePolicyVersion?: number;
  id: string;
  name: string;
  slug: string;
  deploymentProfile: DeploymentProfile;
  status: 'active' | 'suspended';
  /** Owning partner. Absent = platform-owned (standalone) tenant. */
  partnerId?: string;
  /** Platform-controlled: may this tenant connect BYO custom models? */
  allowCustomModels?: boolean;
  /** Platform-controlled: policy-gated MCP connectors (e.g. 'HWP_Operate')
   *  this tenant may install/use. Absent or empty = none. Flows into the merged
   *  policy as org.active_mcp_connectors. */
  activeMcpConnectors?: string[];
  allowedPermissionModes?: AgentPermissionMode[];
  /** Admin-controlled: deep-agent child tools/categories that require HITL
   *  approval. Flows into the merged policy as
   *  `veluga.deep_agent.approval_tools` (org tier). Accepts categories
   *  (`delete`, `edit`, `external`, `hwp`, `write`) or exact tool names
   *  (`hwp_save`, `mcp__…`). Absent = the merge default set. */
  deepAgentApprovalTools?: string[];
  /** Optional tenant rollout threshold for Deep Agent credit preflight. */
  deepAgentMinStartCreditMillis?: number;
  /** Platform-controlled: social login gating. disabled(default)=no social
   *  buttons rendered on desktop, optional=social + password. R-1. */
  socialLoginMode?: SocialLoginMode;
  /** Platform-controlled master gate for public self-signup. */
  selfSignupAllowed?: boolean;
  /** Partner-controlled tenant gate, or platform-controlled for unowned tenants. */
  selfSignupEnabled?: boolean;
  /** Tenant-level gate for managed organization SSO. This is independent from
   *  social login so either surface can be enabled without changing the other. */
  organizationSsoMode?: OrganizationSsoMode;
  /** Tenant-admin editable customer-facing aliases; model ids and routing stay unchanged. */
  customerModelLabels?: Record<string, { providerName?: string; modelName?: string }>;
  /** Data residency and provider-retention controls for external media routes. */
  requiredDataRegion?: string;
  requireZdr?: boolean;
  requireMam?: boolean;
  /** Org-tier external API policy. Omitted is deny. */
  externalApis?: 'allow' | 'deny';
  generativeUi?: 'allow' | 'deny';
  /** Platform-controlled models whose provider cost is absorbed by Veluga. */
  freeTierModelIds?: string[];
}

export interface SignupConfig {
  tenants: Array<{ slug: string; name: string }>;
  /** The platform-selected tenant is active, opted in through both gates, and
   *  the service can deliver signup mail. The desktop entry point remains
   *  visible when false, but submission is unavailable. */
  signupEnabled: boolean;
  defaultTenant?: { id: string; slug: string; name: string };
  /** Can the server actually deliver mail? Independent of the per-tenant
   *  self-signup gate — `tenants` is also empty when mail works but no tenant
   *  opted in — and the only basis for showing the password-reset entry point.
   *  A new client against an old server reads `undefined` here and hides the
   *  link, which is the safe direction: that server cannot mail either. */
  passwordResetEnabled: boolean;
  /** Present only when the verification-code flow is fully ready. */
  passwordResetMethod?: 'email_code_v1';
}

export interface SignupInput {
  /** Older clients may explicitly select an eligible tenant. New desktop
   *  clients omit this and let the server resolve the platform default by id. */
  tenantSlug?: string;
  email: string;
  /** Display name for the provisioned user. Collected by the desktop signup
   *  screen and by API integrations; when absent the server falls back to the
   *  email local part. */
  name?: string;
  locale?: 'ko' | 'en';
}

export interface DefaultSignupTenantSetting {
  tenantId: string | null;
  updatedAt?: string;
}

export interface SignupAccepted {
  ok: true;
  outcome?: 'already_registered';
}

export interface SelfSignupFundingWarning {
  fundedRemainingMillis: number;
  theoreticalExposureMillis: number;
}

export type TenantSelfSignupResult = Tenant & {
  fundedWarning?: SelfSignupFundingWarning;
};

// ── Social login ───────────────────────────────────────────────────────────

/** Platform-controlled gating for a tenant's social login. */
export type SocialLoginMode = 'disabled' | 'optional';

/** Social provider kinds. google is implemented; kakao/naver are placeholders
 *  (config can be saved, but the verifier throws until implemented). R-4/R-5. */
export type SocialProviderKind = 'google' | 'kakao' | 'naver';

/** The social provider kinds, as a runtime list — single source of truth for
 *  service-api validation and admin-web UI (mirror of WEB_SEARCH_NATIVE_PROVIDERS). */
export const SOCIAL_PROVIDER_KINDS: readonly SocialProviderKind[] = [
  'google',
  'kakao',
  'naver',
];

/** Runtime type guard for {@link SocialProviderKind}. */
export function isSocialProviderKind(
  value: string
): value is SocialProviderKind {
  return (SOCIAL_PROVIDER_KINDS as readonly string[]).includes(value);
}

/** Public (non-secret) provider config. The client_secret is never here — it is
 *  stored in encrypted_secrets and referenced server-side by secret_ref. */
export interface SocialProviderConfig {
  clientId?: string;
  redirectUri?: string;
  /** Google Workspace `hd` restriction. */
  hostedDomain?: string;
}

/** A tenant's social provider configuration (admin-facing, secret-free). The
 *  secret_ref lives only in the service-api-internal record type. */
export interface TenantSocialProvider {
  id: string;
  tenantId: string;
  provider: SocialProviderKind;
  displayName: string;
  enabled: boolean;
  config: SocialProviderConfig;
  allowJitProvisioning: boolean;
  allowedEmailDomains: string[];
  /** Whether a client_secret is stored (derived; never the secret itself). */
  hasSecret?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Admin response for a tenant's social config (gating + providers, secret-free). */
export interface TenantSocialConfig {
  socialLoginMode: SocialLoginMode;
  providers: TenantSocialProvider[];
}

/** /v1/auth/sso/providers public response — drives the desktop login buttons.
 *  Secret-free. R-2: empty array when no active provider. */
export interface SocialProviderPublic {
  providerId: string;
  provider: SocialProviderKind;
  displayName: string;
}

/** External identity mapping (identities table record). */
export interface Identity {
  tenantId: string;
  userId: string;
  providerId: string;
  provider: SocialProviderKind;
  subject: string;
  email?: string;
  attributes: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ── Organization SSO ───────────────────────────────────────────────────────

/** Tenant-level organization SSO gate. `required` applies only to ordinary
 *  tenant-user login; platform/partner and break-glass admin paths stay
 *  separate. */
export type OrganizationSsoMode = 'disabled' | 'optional' | 'required';

export const ORGANIZATION_SSO_MODES: readonly OrganizationSsoMode[] = [
  'disabled',
  'optional',
  'required',
];

export function isOrganizationSsoMode(
  value: string
): value is OrganizationSsoMode {
  return (ORGANIZATION_SSO_MODES as readonly string[]).includes(value);
}

/** Supported organization-managed identity protocols. These are deliberately
 *  separate from consumer/social provider brands. */
export type OrganizationSsoKind = 'entra_oidc' | 'saml2' | 'verification_api';

export const ORGANIZATION_SSO_KINDS: readonly OrganizationSsoKind[] = [
  'entra_oidc',
  'saml2',
  'verification_api',
];

export function isOrganizationSsoKind(
  value: string
): value is OrganizationSsoKind {
  return (ORGANIZATION_SSO_KINDS as readonly string[]).includes(value);
}

/** Untrusted identifier hints accepted only by a verification API connection. */
export type OrganizationSsoIdentifierType =
  'id' | 'index' | 'external_id' | 'login_id' | 'email';

export const ORGANIZATION_SSO_IDENTIFIER_TYPES: readonly OrganizationSsoIdentifierType[] =
  ['id', 'index', 'external_id', 'login_id', 'email'];

export function isOrganizationSsoIdentifierType(
  value: string
): value is OrganizationSsoIdentifierType {
  return (ORGANIZATION_SSO_IDENTIFIER_TYPES as readonly string[]).includes(
    value
  );
}

/** Secret-free public projection used to render organization SSO login choices. */
export interface OrganizationSsoConnectionPublic {
  connectionId: string;
  kind: OrganizationSsoKind;
  displayName: string;
  requiresIdentifier: boolean;
  allowedIdentifierTypes?: OrganizationSsoIdentifierType[];
}

/** Secret-free public discovery envelope. The mode is returned separately so
 * a required tenant stays fail-closed even if its connection list is empty. */
export interface OrganizationSsoDiscovery {
  organizationSsoMode: OrganizationSsoMode;
  connections: OrganizationSsoConnectionPublic[];
}

export type OrganizationSsoProvisioningMode =
  | 'invite_only'
  | 'jit_restricted';

export type OrganizationSsoConnectionStatus =
  | 'draft'
  | 'tested'
  | 'enabled'
  | 'error';

/** Secret-free administrator projection. Secret presence is represented only
 * by booleans; secret refs and plaintext never cross the service boundary. */
export interface OrganizationSsoConnectionAdmin {
  id: string;
  tenantId: string;
  connectionKey: string;
  kind: OrganizationSsoKind;
  displayName: string;
  enabled: boolean;
  status: OrganizationSsoConnectionStatus;
  config: Record<string, unknown>;
  hasSecret: boolean;
  hasPrivateKey: boolean;
  hasClientCertificate: boolean;
  claimMapping: Record<string, unknown>;
  provisioningMode: OrganizationSsoProvisioningMode;
  allowedEmailDomains: string[];
  lastTestAt?: string;
  lastTestStatus?: 'passed' | 'failed';
  lastTestErrorCode?: string;
  lastSuccessAt?: string;
  identityCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantOrganizationSsoConfig {
  organizationSsoMode: OrganizationSsoMode;
  connections: OrganizationSsoConnectionAdmin[];
}

export interface OrganizationSsoConnectionWrite {
  connectionKey: string;
  kind: OrganizationSsoKind;
  displayName: string;
  config: Record<string, unknown>;
  secret?: string;
  privateKey?: string;
  clientCertificate?: string;
  claimMapping?: Record<string, unknown>;
  provisioningMode?: OrganizationSsoProvisioningMode;
  allowedEmailDomains?: string[];
}

export interface OrganizationSsoConnectionPatch {
  displayName?: string;
  config?: Record<string, unknown>;
  secret?: string;
  privateKey?: string;
  clientCertificate?: string;
  claimMapping?: Record<string, unknown>;
  provisioningMode?: OrganizationSsoProvisioningMode;
  allowedEmailDomains?: string[];
}

export interface OrganizationSsoConnectionTestInput {
  identifierType?: OrganizationSsoIdentifierType;
  identifier?: string;
}

export interface OrganizationSsoConnectionTestResult {
  ok: boolean;
  testedAt: string;
  errorCode?: string;
}

/** Protocol-neutral verified identity. Only the Service API creates this after
 *  validating the external response; clients must never construct or trust it. */
export interface VerifiedOrganizationIdentity {
  connectionId: string;
  subject: string;
  email?: string;
  emailVerified: boolean;
  displayName?: string;
  attributes: Record<string, unknown>;
}

/** Stable organization SSO failure catalog shared by HTTP and in-process
 *  clients. Account/tenant/entitlement failures continue to use the existing
 *  Veluga authorization errors after identity mapping. */
export type OrganizationSsoErrorCode =
  | 'organization_sso_disabled'
  | 'organization_sso_connection_not_found'
  | 'organization_sso_connection_disabled'
  | 'organization_sso_connection_misconfigured'
  | 'organization_sso_invalid_request'
  | 'organization_sso_identifier_not_allowed'
  | 'organization_sso_state_mismatch'
  | 'organization_sso_nonce_mismatch'
  | 'organization_sso_protocol_request_mismatch'
  | 'organization_sso_tenant_mismatch'
  | 'organization_sso_issuer_mismatch'
  | 'organization_sso_audience_mismatch'
  | 'organization_sso_response_expired'
  | 'organization_sso_replayed'
  | 'organization_sso_denied'
  | 'organization_sso_upstream_unavailable'
  | 'organization_account_not_provisioned'
  /** The IdP asserted an address whose domain is not in the connection's
   *  `allowedEmailDomains`, so it may not select a Veluga account. Distinct from
   *  `organization_account_not_provisioned`: the operator action is "add the
   *  domain", not "invite the user". */
  | 'organization_email_domain_not_allowed'
  | 'organization_identity_conflict'
  /** 429 from the pre-auth throttle in front of the unauthenticated routes.
   *  Carries `details.retryAfterSeconds` plus a `retry-after` header. */
  | 'organization_sso_rate_limited';

export interface ServiceUser {
  id: string;
  email: string;
  name: string;
  status: 'invited' | 'active' | 'disabled';
}

/** Non-secret projection of a password credential. The secret-bearing record
 *  (with the hash/salt) lives server-side in `@veluga/service-api` and is never
 *  part of the client-shared type surface. */
export interface PasswordCredentialState {
  userId: string;
  tenantId: string;
  mustChangePassword: boolean;
  updatedAt: string;
}

/** Server-persisted password-reset state. Only digests and opaque correlation
 * values are stored; raw verification codes and reset grants never enter this
 * record. A decoy challenge has neither tenantId nor userId. */
export interface PasswordResetChallenge {
  id: string;
  emailKey: string;
  tenantId?: string;
  userId?: string;
  codeDigest: string;
  codeIssuedAt: string;
  codeExpiresAt: string;
  issueCount: number;
  verifyAttemptCount: number;
  nextResendAt: string;
  verifiedAt?: string;
  resetGrantDigest?: string;
  resetGrantExpiresAt?: string;
  consumedAt?: string;
  invalidatedAt?: string;
  flowExpiresAt: string;
  credentialFingerprint?: string;
  generation: number;
  createdAt: string;
  updatedAt: string;
}

/** Bounded rolling-window timestamps used to prevent a new reset handle from
 * resetting email-level issue and verification limits. */
export interface PasswordResetBudget {
  emailKey: string;
  issueTimestamps: string[];
  verifyTimestamps: string[];
  updatedAt: string;
}

export interface PasswordLoginInput {
  sessionProtocol?: import('./session.js').SessionProtocol;
  email: string;
  password: string;
  tenantId?: string;
}

export interface PasswordResetCodeRequestInput {
  email: string;
  locale?: 'ko' | 'en';
}

export interface PasswordResetCodeAccepted {
  ok: true;
  handle: string;
  nextResendAt: string;
}

export interface PasswordResetCodeResendInput {
  handle: string;
  locale?: 'ko' | 'en';
}

export interface PasswordResetCodeResent {
  ok: true;
  nextResendAt: string;
}

export interface PasswordResetCodeVerifyInput {
  handle: string;
  code: string;
}

export interface PasswordResetCodeVerified {
  resetGrant: string;
  expiresAt: string;
}

export interface PasswordResetCodeConfirmInput {
  handle: string;
  resetGrant: string;
  newPassword: string;
}

/** Renderer-safe reset flow views. Opaque handle/grant values stay in Electron main. */
export interface PasswordResetCodeRequestView {
  ok: true;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface PasswordResetCodeResendView {
  ok: true;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface PasswordResetCodeVerifyView {
  ok: true;
  expiresInSeconds: number;
}

export interface PasswordResetCodeConfirmView {
  ok: true;
}

export interface Membership {
  tenantId: string;
  userId: string;
  roles: ServiceRole[];
  groupIds: string[];
  clearance?: Clearance;
}

export interface Group {
  id: string;
  tenantId: string;
  name: string;
  /** @deprecated Reserved; no runtime policy effect. */
  defaultPolicyTemplateId?: string;
}

export type DownloadChannel = 'stable' | 'beta' | 'internal';
export type DesktopPlatform =
  'darwin-arm64' | 'darwin-x64' | 'win32-x64' | 'linux-x64';

export const IMAGE_GENERATION_FEATURE = 'image-generation';
export const IMAGE_GENERATION_REFERENCE_FEATURE = 'image-generation-reference';
export const IMAGE_GENERATION_REFERENCE_MODELS = [
  'openai/gpt-image-1',
  'openai/gpt-image-2',
] as const;
export const VIDEO_GENERATION_FEATURE = 'video-generation';

export interface Entitlement {
  tenantId: string;
  userId: string;
  seatStatus: 'assigned' | 'unassigned' | 'suspended';
  desktopAccess: boolean;
  allowedModelIds: string[];
  allowedFeatures: string[];
  downloadChannels: DownloadChannel[];
  allowedPlatforms: DesktopPlatform[];
  minimumDesktopVersion?: string;
}

// ── Model catalog & provider backend contracts ────────────────────────────

export type PlatformProviderKeyProvider =
  'openai' | 'anthropic' | 'gemini' | 'qwen' | 'glm' | 'deepseek' | 'grok' | 'openrouter' | 'minimax';

/** The native web-search providers, as a runtime list. Single source of truth —
 *  service-api validation, bootstrap availability, and admin-web UI all derive
 *  their provider set from this so a new provider is one edit, not three. */
export const WEB_SEARCH_NATIVE_PROVIDERS: readonly WebSearchNativeProvider[] = [
  'openai',
  'anthropic',
  'gemini',
];

/** Runtime type guard for {@link WebSearchNativeProvider}. */
export function isWebSearchNativeProvider(
  value: string
): value is WebSearchNativeProvider {
  return (WEB_SEARCH_NATIVE_PROVIDERS as readonly string[]).includes(value);
}

export type GatewayBackendKind =
  | 'litellm-proxy'
  | 'openai-compatible-direct'
  | 'anthropic-compatible-direct'
  | 'gemini-compatible-direct'
  | 'custom-executor';

export type RouteManagedBy =
  'veluga' | 'tenant-admin' | 'partner-admin' | 'on-prem-operator';
export type BackendStatus = 'active' | 'disabled' | 'unhealthy';

export interface GatewayBackend {
  id: string;
  tenantId?: string;
  kind: GatewayBackendKind;
  displayName: string;
  /** Connection URL ref — where the gateway CONNECTS (for `litellm-proxy`
   *  backends this is the LiteLLM endpoint, not the provider). Secret-bearing
   *  alias; never sent to desktop. */
  baseUrlRef: string;
  /** Connection credential ref — the bearer the gateway authenticates WITH
   *  (for `litellm-proxy` backends this is the infra master key, never a
   *  tenant provider key). Secret manager / key vault reference. */
  credentialRef: string;
  /** Tenant upstream provider base URL ref, forwarded to LiteLLM per-request
   *  as `api_base` (client-side credential pass-through). Never sent to desktop. */
  upstreamBaseUrlRef?: string;
  /** Tenant upstream provider key ref, forwarded per-request as `api_key` —
   *  never used as the connection bearer and never stored in LiteLLM. */
  upstreamCredentialRef?: string;
  deploymentProfile: DeploymentProfile;
  managedBy: RouteManagedBy;
  status: BackendStatus;
  telemetryMode: 'veluga-only' | 'backend-native' | 'disabled';
}

export interface ModelRouteBackend {
  backendId: string;
  protocol: ProviderProtocol;
  /** e.g. veluga.gpt-4o.secure or openai/gpt-4o — never exposed to desktop */
  litellmModelName?: string;
  /** secret/internal alias — never exposed to desktop */
  upstreamModelRef?: string;
  weight?: number;
  priority?: number;
  healthCheckRef?: string;
}

export type PartnerCustomModelProtocol = 'openai-compatible';

export interface PartnerCustomModelRouteBackend
  extends Omit<ModelRouteBackend, 'protocol'> {
  protocol: PartnerCustomModelProtocol;
}

export type RouterKind = 'none' | 'veluga-rule' | 'routellm' | 'veluga-learned';
export type ModelTier = 'weak' | 'medium' | 'strong' | 'local';

export type ModelRoutingCandidate =
  | {
      tier: ModelTier;
      routeBackend: ModelRouteBackend;
      catalogModelId?: never;
    }
  | {
      tier: ModelTier;
      catalogModelId: string;
      routeBackend?: never;
    };

export interface AutoRoutingConfig {
  objective: 'balanced' | 'cost' | 'quality';
  providerMode: 'mixed-provider' | 'single-provider';
  deploymentMode: 'shadow' | 'runtime';
  enabled: boolean;
  thresholdOverride?: number;
  allowedTaskKinds?: AllowedAdaptiveTaskKind[];
  judge?: AutoRoutingJudgeConfig;
}

export interface AutoRoutingJudgeConfig {
  mode: 'off' | 'shadow' | 'runtime';
  catalogModelId?: string;
}

export interface ModelRoutingPolicy {
  mode: VelugaRouteMode;
  router?: RouterKind;
  candidates: ModelRoutingCandidate[];
  auto?: AutoRoutingConfig;
  fallback?: {
    onProviderError?: 'same-tier' | 'strong' | 'deny';
    onSchemaFailure?: 'strong' | 'deny';
    onToolFailure?: 'strong' | 'deny';
    onLowConfidence?: 'strong' | 'deny';
  };
  safety: {
    allowAdaptiveForRisk: Array<'low' | 'medium' | 'high'>;
    disallowForDataClassifications: Array<'confidential' | 'secret'>;
    requireStrongForFinalAnswer: boolean;
    requireStrongForCompliance: boolean;
  };
}

/** Reserved pseudo-tenant id for platform-scoped (tenant-agnostic) secrets and
 *  audit rows. Canonical source of truth — service-api re-exports this so every
 *  consumer (encrypted secret store, gateway, persistence) shares one literal. */
export const PLATFORM_TENANT_ID = '__platform__';
export const PLATFORM_GEMINI_SEARCH_KEY_NAME = 'gemini-search-key';
/** Derived from {@link PLATFORM_TENANT_ID} + name in the same `secret://tenant/<id>/<name>`
 *  shape that `secretRef()` emits (both segments are encode-safe), so the ref can
 *  never drift from the tenant-id constant. */
export const PLATFORM_GEMINI_SEARCH_KEY_REF = `secret://tenant/${PLATFORM_TENANT_ID}/${PLATFORM_GEMINI_SEARCH_KEY_NAME}`;

export interface TenantWebSearchBackend {
  id: string;
  tenantId: string;
  provider: WebSearchNativeProvider;
  status: 'active' | 'disabled';
  credentialRef: string;
  baseUrlRef?: string;
  maxUsesDefault: number;
  /** Reserved for stricter tenant rollouts; v1 stores tenant-level backends only. */
  allowedModelIds?: string[];
  createdAt: number;
  updatedAt: number;
}

export type TenantWebSearchBackendSummary = Omit<
  TenantWebSearchBackend,
  'credentialRef' | 'baseUrlRef' | 'allowedModelIds'
>;

export type MediaGenerationProvider = 'openai' | 'gemini' | 'openrouter';

export interface TenantMediaRoute {
  id: string;
  tenantId: string;
  kind: 'image' | 'video';
  provider: MediaGenerationProvider;
  status: 'active' | 'disabled';
  credentialRef: string;
  baseUrlRef?: string;
  model: string;
  endpointRegion?: string;
  supportsZdr: boolean;
  supportsMam: boolean;
  providerOptions?: Record<string, unknown>;
  maxUsesDefault: number;
  createdAt: number;
  updatedAt: number;
}

export type TenantMediaRouteSummary = Omit<TenantMediaRoute, 'credentialRef' | 'baseUrlRef'>;

export interface DesktopImageGenerationAvailability {
  enabled: boolean;
  referenceInputEnabled?: boolean;
  maxUsesDefault: number;
}

export interface DesktopVideoGenerationAvailability {
  enabled: boolean;
  maxUsesDefault: number;
}

export type MediaGenerationProviderStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export type MediaGenerationDeliveryStatus =
  | 'pending'
  | 'available'
  | 'downloaded'
  | 'discarded'
  | 'expired';

/** Durable server-side record. providerJobId is never returned to desktop clients. */
export interface MediaGenerationJob {
  id: string;
  tenantId: string;
  userId: string;
  groupIds: string[];
  sessionId: string;
  requestId: string;
  routeId: string;
  provider: 'openai';
  providerJobId: string;
  model: string;
  seconds: 4 | 8 | 12;
  size: '720x1280' | '1280x720' | '1024x1792' | '1792x1024';
  providerStatus: MediaGenerationProviderStatus;
  progress?: number;
  clientCancelledAt?: string;
  deliveryStatus: MediaGenerationDeliveryStatus;
  resultExpiresAt?: string;
  errorCode?: string;
  settlementStatus: 'reserved' | 'settling' | 'committed' | 'released';
  settlementClaimedAt?: string;
  credit: import('./quota.js').CreditComponentSnapshot;
  createdAt: string;
  updatedAt: string;
}

export interface AutoRoutingProfileWrite {
  candidates: Array<{ catalogModelId: string; tier: ModelTier }>;
  objective: AutoRoutingConfig['objective'];
  providerMode: AutoRoutingConfig['providerMode'];
  deploymentMode: AutoRoutingConfig['deploymentMode'];
  enabled: boolean;
  thresholdOverride?: number;
  allowedTaskKinds?: AllowedAdaptiveTaskKind[];
  judge?: AutoRoutingJudgeConfig;
}

export type AutoRoutingReasonCode =
  | 'candidate_missing'
  | 'candidate_inactive'
  | 'candidate_not_routable'
  | 'candidate_auto_nested'
  | 'candidate_duplicate'
  | 'tier_cardinality_invalid'
  | 'provider_mismatch'
  | 'billing_mode_mismatch'
  | 'rate_missing'
  | 'thinking_auto_unsupported'
  | 'strong_candidate_missing'
  | 'judge_model_missing'
  | 'judge_model_inactive'
  | 'judge_model_not_routable'
  | 'judge_structured_output_unsupported'
  | 'judge_rate_missing';

export interface AutoRoutingEffectiveView {
  threshold: number;
  thresholdSource: 'objective' | 'override';
  mediumBandThreshold: number;
  allowedTaskKinds: AllowedAdaptiveTaskKind[];
  deniedTaskKinds: AdaptiveTaskKind[];
  judge: AutoRoutingJudgeConfig;
}

export interface AutoRoutingProfileView {
  tenantId: string;
  catalogItem?: VelugaModelCatalogItem;
  status: 'missing' | 'valid' | 'degraded';
  reasonCodes: AutoRoutingReasonCode[];
  updatedAt?: string;
  updatedBy?: string;
  candidates: Array<{
    catalogModelId: string;
    tier: ModelTier;
    provider?: VelugaModelProvider;
    rateAvailable: boolean;
  }>;
  availableCandidates: Array<{
    catalogModelId: string;
    displayName: string;
    provider: VelugaModelProvider;
    status: VelugaModelCatalogItem['status'];
    routeActive: boolean;
    billingMode: BillingMode;
    rateAvailable: boolean;
    billingRate?: {
      inputUsdPerMillion: number;
      outputUsdPerMillion: number;
      cachedInputUsdPerMillion?: number;
      cacheWriteInputUsdPerMillion?: number;
      marginMultiplier: number;
    };
    thinkingAuto: boolean;
    capabilities: VelugaModelCapabilities;
    limits: VelugaModelCatalogItem['limits'];
  }>;
  effective: AutoRoutingEffectiveView;
}

export interface AutoRoutingPreviewRequest {
  mode?: 'local' | 'judge';
  prompt?: string;
  taskKind?: AdaptiveTaskKind;
  risk?: 'low' | 'medium' | 'high';
  requiresVision?: boolean;
  requiresTools?: boolean;
  estimatedInputTokens?: number;
  finalAnswer?: boolean;
  compliance?: boolean;
}

export interface AutoRoutingPreviewResult {
  profile: AutoRoutingProfileView;
  selectedCatalogModelId?: string;
  selectedTier?: ModelTier;
  thinkingStrategy?: ThinkingAutoCapability['strategy'];
  reason: string;
  effective: AutoRoutingEffectiveView;
  score?: number;
  threshold?: number;
  localOnly: boolean;
  judge?: {
    output?: {
      taskKind?: AdaptiveTaskKind;
      riskLevel?: 'low' | 'medium' | 'high';
      difficulty?: number;
    };
    outcome: string;
    inputTokens?: number;
    outputTokens?: number;
    latencyMs?: number;
    costCreditMillis?: number;
  };
}

export interface VelugaModelCatalogItem {
  /** Absent means legacy chat. Voice models never enter a text runner. */
  interactionKind?: ModelInteractionKind;
  realtimeVoice?: RealtimeVoiceMetadata;
  speech?: SpeechModelMetadata;
  /** Veluga model id shown to desktop/admin — the only id used as `model` */
  id: string;
  provider: VelugaModelProvider; // UI grouping only
  displayName: string;
  description?: string;
  routeKind: RouteKind;
  capabilities: VelugaModelCapabilities;
  thinkingLevels?: ThinkingLevel[];
  limits: {
    contextWindow: number;
    maxOutputTokens: number;
    maxInputTokens?: number;
  };
  policyTags: string[];
  routing: ModelRoutingPolicy;
  defaultForProvider?: boolean;
  status: 'active' | 'deprecated' | 'disabled';
  /** How usage of this model settles. Defaults to 'platform-credit' (priced via
   *  credit rates). BYO/custom models can be 'byo-unbilled' (never charged) or
   *  'byo-token-budget'. */
  billingMode?: BillingMode;
}

export interface ModelRoute {
  id: string;
  tenantId: string;
  catalogModelId: string;
  routeKind: RouteKind;
  providerProtocol: ProviderProtocol;
  /** secret-bearing URL alias — not exposed to desktop users */
  endpointRef: string;
  /** secret manager / key vault reference */
  credentialRef: string;
  managedBy: RouteManagedBy;
  status: BackendStatus;
}

/** Operator-entered pricing for a partner custom model, in the same
 *  USD-per-million-tokens unit as the CreditRate engine. This is the *input*
 *  shape — `marginMultiplier` defaults to 1 when omitted. Sibling of
 *  `PlatformProviderModelRateInput`. */
export interface PartnerCustomModelPricingInput {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion?: number;
  cacheWriteInputUsdPerMillion?: number;
  marginMultiplier?: number;
  note?: string;
}

/** The normalized/persisted pricing shape. `marginMultiplier` is always set.
 *  Both rates 0 means the model is free (`byo-unbilled`); both positive means
 *  it settles against platform credits. A mixed 0 is rejected. */
export interface PartnerCustomModelPricing {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion?: number;
  cacheWriteInputUsdPerMillion?: number;
  marginMultiplier: number;
  note?: string;
}

/** Write-only provider credential for a partner custom model. Declared here so
 *  `service-client` and `service-api/admin/shared.ts` alias one definition
 *  rather than each keeping their own copy. */
export interface PartnerCustomModelCredentialInput {
  /** Write-only provider API key. Stored as an encrypted secret ref. */
  apiKey: string;
  /** Provider OpenAI-compatible base URL, for example https://api.deepseek.com. */
  baseUrl: string;
  /** Optional dedicated LiteLLM endpoint. Defaults to the shared infra ref. */
  litellmBaseUrl?: string;
}

/** True when both rates are zero — usage is metered but no credits are deducted. */
export function isFreePartnerPricing<
  T extends { inputUsdPerMillion: number; outputUsdPerMillion: number },
>(
  pricing: T,
): boolean {
  return pricing.inputUsdPerMillion === 0 && pricing.outputUsdPerMillion === 0;
}

/**
 * Derive the persisted `billing_mode` from pricing.
 *
 * Lives in shared-types so the admin-web form and the service normalizer share
 * one rule rather than drifting: a UI that showed "free" while the server stored
 * `platform-credit` would be a billing bug the operator could not see. Kept
 * dependency-free for that reason — nothing here may import server code.
 *
 * `byo-token-budget` is never derived. That mode is a live enforcement path (the
 * gateway applies raw-token pools only under it), so converging a record to
 * `byo-unbilled` would silently drop its token cap.
 */
export function partnerPricingBillingMode<
  T extends { inputUsdPerMillion: number; outputUsdPerMillion: number },
>(
  pricing: T,
): Extract<BillingMode, 'byo-unbilled' | 'platform-credit'> {
  return isFreePartnerPricing(pricing) ? 'byo-unbilled' : 'platform-credit';
}

export interface PartnerCustomModelDefinition {
  partnerId: string;
  modelKey: string;
  displayName: string;
  description?: string;
  protocol: PartnerCustomModelProtocol;
  routeBackend: PartnerCustomModelRouteBackend;
  capabilities: VelugaModelCapabilities;
  thinkingLevels?: ThinkingLevel[];
  limits: VelugaModelCatalogItem['limits'];
  /** Operator-entered rates. Absent on legacy rows registered before the
   *  pricing contract — those keep their stored `billingMode` semantics. */
  pricing?: PartnerCustomModelPricing;
  /** Derived from `pricing` when present (0/0 -> byo-unbilled, positive ->
   *  platform-credit); otherwise the previously stored value. Kept as a
   *  persisted column for gateway/catalog compatibility — never an input of
   *  the pricing contract. `byo-token-budget` is not a derivable value. */
  billingMode?: BillingMode;
  status: 'active' | 'disabled';
  /** Monotonic, server-owned. Starts at 1 and increments on every accepted
   *  write; used for compare-and-swap on edits. Never client-supplied. */
  revision: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Managed broker registration of an externally-owned source repo
 * (skill-agent-distribution PR5). Scope is platform-global (both ids unset),
 * partner, or tenant. Credential storage (credentialRef) lands in PR7.
 */
export interface CapabilitySourceRecord {
  id: string;
  partnerId?: string;
  tenantId?: string;
  sourceKind: 'skill-pack' | 'agent-team' | 'mixed';
  sourceKey: string; // owner/repo@ref normalized key
  displayName: string;
  description?: string;
  repoUrl: string;
  ref: string;
  indexPath: string;
  visibility: 'public' | 'private';
  authMethod?: 'github-app' | 'stored-token' | 'none';
  credentialRef?: string;
  commitPin?: string;
  requireSignature: boolean;
  requireCommitPin: boolean;
  lastSyncedAt?: string;
  lastSyncStatus?: string;
  status: 'active' | 'disabled' | 'error';
  createdAt: string;
  updatedAt: string;
}

/** A broker-mirrored bundle for one entry version (skill-agent-distribution PR7).
 *  The objectPath is the private Storage key — never returned to clients; only a
 *  short-lived signed URL is. */
export interface CapabilityMirrorRecord {
  sourceId: string;
  entryId: string;
  version: string;
  objectPath: string;
  bundleSha256: string;
  bundleSizeBytes: number;
  /** Re-signed ManagedCapabilityManifest (SignedManifest payload), when signed. */
  signedManifest?: object;
  mirroredAt: string;
}

/** A source assigned (exposed) to a tenant. */
export interface CapabilitySourceAssignment {
  tenantId: string;
  sourceId: string;
  enabled: boolean;
  assignedAt: string;
  assignedBy: string;
}

/** Per-entry enable toggle (overview §5.4). A missing row = enabled (opt-out). */
export interface CapabilityEntryToggle {
  tenantId: string;
  entryId: string; // index.json entry's global id
  enabled: boolean;
  sourceId?: string; // audit/join convenience; may change on precedence moves
  updatedAt: string;
  updatedBy: string;
}

export interface TenantModelAccess {
  tenantId: string;
  subjectType?: 'user' | 'group';
  subjectId?: string;
  allowedModelIds: string[];
  defaultModelId: string;
  selectedModelId?: string;
  disabledReasonByModelId?: Record<string, string>;
}

export interface TenantAssignment {
  tenantId: string;
  allowedModelIds: string[];
  /** Models inherited by every active member; must remain within allowedModelIds. */
  defaultMemberModelIds: string[];
  /** Tenant-wide default considered at login after an explicit user selection. */
  loginDefaultModelId?: string;
  /** Assigned platform-credit models that do not charge tenant credit. */
  freeTierModelIds?: string[];
  /** Assigned models with a non-zero active provider rate (free-tier warning candidates). */
  paidFreeTierModelIds?: string[];
  /** Tenant-visible catalog model ids that can be checked into the assignment. */
  assignableModelIds?: string[];
  updatedAt: string;
}

export interface TenantLoginDefaultModelView {
  tenantId: string;
  defaultMemberModelIds: string[];
  loginDefaultModelId?: string;
  candidates: DesktopModelView[];
  updatedAt?: string;
}

export interface UpdateTenantLoginDefaultModelRequest {
  loginDefaultModelId: string | null;
  idempotencyKey: string;
}

/** Read-side view of a tenant's MCP connector activation — the connector twin
 *  of TenantAssignment. `assignableMcpConnectors` is the bounded choice set:
 *  the partner's connector scope ∩ catalog for a partner-owned tenant, or the
 *  full catalog for a standalone tenant. */
export interface TenantMcpConnectors {
  tenantId: string;
  activeMcpConnectors: string[];
  assignableMcpConnectors: string[];
}

export interface ProviderKeySummary {
  provider: PlatformProviderKeyProvider;
  hasCredential: boolean;
  maskedKey?: string;
  baseUrl?: string;
  litellmBaseUrl?: string;
  updatedAt?: string;
}

export interface RegisterProviderKeyInput {
  tenantId: string;
  provider: PlatformProviderKeyProvider;
  apiKey: string;
  baseUrl?: string;
  litellmBaseUrl?: string;
}

export interface ProviderKeyRegistrationResult {
  provider: PlatformProviderKeyProvider;
  backendId: string;
  assignment: TenantAssignment;
}

export interface ProviderKeyRemovalResult {
  provider: PlatformProviderKeyProvider;
  tenantId: string;
  modelIds: string[];
  assignment: TenantAssignment;
}

export interface PlatformWebSearchKeySummary {
  provider: Extract<WebSearchNativeProvider, 'gemini'>;
  hasCredential: boolean;
}

/** Classification of a server-side provider-key connection test. Mirrors the
 *  desktop `ApiTestResult` taxonomy, scoped to platform providers (no Ollama). */
export type ProviderKeyTestErrorType =
  | 'no_credential'
  | 'unauthorized'
  | 'not_found'
  | 'rate_limited'
  | 'server_error'
  | 'network_error'
  | 'unknown';

export interface TestProviderKeyInput {
  tenantId: string;
  provider: PlatformProviderKeyProvider;
  /** Optional unsaved API key to probe from the admin form (pre-registration
   *  test). When present the stored credential is not consulted. */
  apiKey?: string;
  /** Optional unsaved provider base URL accompanying `apiKey`. */
  baseUrl?: string;
}

export interface TestLiteLLMConnectionInput extends TestProviderKeyInput {
  /** Optional unsaved LiteLLM endpoint to probe from the admin form. */
  litellmBaseUrl?: string;
}

export interface ProviderKeyTestResult {
  provider: PlatformProviderKeyProvider;
  ok: boolean;
  /** Round-trip latency of the probe, when a request was actually made. */
  latencyMs?: number;
  /** Upstream HTTP status, when there was a response. */
  status?: number;
  errorType?: ProviderKeyTestErrorType;
  /** Short, non-secret diagnostic message. */
  details?: string;
}

// ── Desktop bootstrap ─────────────────────────────────────────────────────

/** Canonical consent versions shared by the service and desktop client. */
export const REQUIRED_CONSENTS: readonly RequiredConsent[] = [
  { kind: 'tos', version: '2026-08-25-placeholder' },
  { kind: 'privacy', version: '2026-08-25-placeholder' },
  { kind: 'log_collection', version: '2026-08-25-placeholder' }
];

export interface GatewayCompatibility {
  chatCompletions: boolean;
  responses: boolean;
  claudeMessages: boolean;
  velugaAgentRuns: boolean;
}

export interface DesktopBootstrapGroup {
  id: string;
  tenantId: string;
  name: string;
}

export interface DesktopQuotaPolicySummary {
  scope: 'tenant' | 'group' | 'user';
  cycle: QuotaCycle;
  limits: {
    requests?: number;
    totalTokens?: number;
  };
  usage: {
    requests: number;
    totalTokens: number;
  };
  resetAt: string;
}

export interface DesktopQuotaSummary {
  /** Backward-compatible most-restrictive values for older desktop clients. */
  remainingRequests?: number;
  remainingTokens?: number;
  resetAt?: string;
  /** Applicable policies in display order: user → group → tenant.
   * Absent when connected to a server version predating this field. */
  policies?: DesktopQuotaPolicySummary[];
}

export interface DesktopBootstrap {
  generativeUi?: { enabled: boolean };
  /** Legacy realtime-only projection for older desktops; its meaning never changes. */
  realtimeVoice?: DesktopRealtimeVoiceAvailability;
  /** Per-kind projection from the same resolver as `/v1/me/voice/availability`. */
  voice?: DesktopVoiceAvailability;
  recommendedModels?: import('./recommended-models.js').RecommendedModelsView;
  user: ServiceUser;
  tenant: Tenant;
  membership: Membership;
  entitlement: Entitlement;
  accountFlags: {
    mustChangePassword: boolean;
    /** Consents that this user must (re)accept. Empty means current; absent
     *  means an older server where the state is unknown. */
    pendingConsents?: RequiredConsent[];
  };
  availableModels: DesktopModelView[];
  webSearch?: DesktopWebSearchAvailability;
  imageGeneration?: DesktopImageGenerationAvailability;
  videoGeneration?: DesktopVideoGenerationAvailability;
  defaultModelId: string;
  selectedModelId: string;
  effectivePolicy: PolicyContextSnapshot;
  gateway: {
    /** Veluga Gateway Facade URL — NOT a LiteLLM URL */
    baseUrl: string;
    token: string;
    expiresAt: string;
    compatibility: GatewayCompatibility;
  };
  minimumDesktopVersion?: string;
  killSwitch?: boolean;
  /** Groups the caller belongs to, resolved to display-safe fields only. */
  groups?: DesktopBootstrapGroup[];
  /**
   * Remaining-quota summary for profile/account display. The server may omit
   * this until a remaining-usage computation is wired; clients hide the section
   * when it is absent. Never a hardcoded placeholder — absence means "unknown".
   */
  quotaSummary?: DesktopQuotaSummary;
  /**
   * Effective prepaid-credit balance for account display, priced in credit
   * units (`creditMillis`). Resolves user allocation → group/tenant per-member
   * default → tenant pool. Omitted when none applies. `unlimited` marks a
   * platform-exempted node — `grantedMillis`/`remainingMillis` are then
   * undefined and clients render an unlimited indicator instead of a balance.
   */
  creditSummary?: {
    enforcementMode?: 'deny' | 'warn';
    scope: 'user' | 'tenant';
    unlimited: boolean;
    consumedMillis: number;
    grantedMillis?: number;
    remainingMillis?: number;
    /** Cycle of the reported figure. Absent means the lifetime pool, which is
     *  how every pre-cycle client already read it. */
    cycle?: QuotaCycle;
    /** When a periodic figure refills (UTC). Absent for lifetime. */
    resetAt?: string;
    provenance?: QuotaPolicyProvenance;
    updatedAt: string;
  };
}

// ── Download manifest ─────────────────────────────────────────────────────

/** The unsigned payload that is fed to the manifest signer. */
export interface UnsignedDownloadManifest {
  channel: DownloadChannel;
  platform: DesktopPlatform;
  version: string;
  url: string;
  sha256: string;
  /** Installer size in bytes — present when the artifact is served from the
   * release registry (absent on the legacy dev fallback manifest). */
  sizeBytes?: number;
  signed: true;
  minimumVersion?: string;
}

/**
 * A signed download manifest. Signing metadata is required: a manifest only
 * exists in this shape once it has been signed, so consumers never have to
 * treat `signature`/`keyId`/`algorithm`/`signedAt` as nullable. Use
 * {@link UnsignedDownloadManifest} for the pre-signing payload.
 */
export interface DownloadManifest extends UnsignedDownloadManifest {
  signature: string;
  keyId: string;
  algorithm: 'ed25519' | 'hmac-sha256';
  signedAt: string;
}
